import enviro from 'enviro';
import * as t from '../utils/schema';
import { Metrics } from '../utils/metrics';
import { devtoolsFlags } from '../features/devtools/flags';
import { getHubSpotWindowInfo } from '../utils/hubspotWindow';
import { getTrustedChildFrameTargets, isMessageFromChildFrame, isTrustedOrigin, readContentWindowOrigin, sendToFrame, sendToSameWindow, sendToTrustedOrigins } from '../utils/trustedPostMessage';
const prevalidatePublish = ({
  value,
  schema,
  context
}) => {
  if (enviro.deployed() && enviro.get('ENV') === 'production') {
    return;
  }
  const check = t.rawExecuteSchema({
    quick: false,
    schemaViolations: []
  }, schema, value);
  if (check.type === 'fail') {
    if (devtoolsFlags.leaderFollowerValidation) {
      console.warn('postMessageStrategy validation error', check.schemaViolations, value, context);
    }
    Metrics.counter('postmessage-strategy-validation-error').increment();
  }
};
const POST_MESSAGE_SOURCE = 'collaboration-sidebar/post-message-strategy';

/**
 * Protocol version. Increment when adding new backwards-incompatible protocol
 * features so followers can gate behaviour on `storeVersion >= N`.
 *
 * Version registry:
 * - 1: leader understands RequestPartialLeaderValueUpdate (follower sends only
 *      changed top-level keys instead of full state on every update)
 */
const STORE_VERSION = 1;
const postMessageEnvelopeSchema = t.union(t.object({
  source: t.literal(POST_MESSAGE_SOURCE),
  key: t.string,
  type: t.literal('ReportNewLeaderValue'),
  payload: t.object({
    value: t.unknown
  })
}), t.object({
  source: t.literal(POST_MESSAGE_SOURCE),
  key: t.string,
  type: t.literal('RequestNewLeaderValue')
}), t.object({
  source: t.literal(POST_MESSAGE_SOURCE),
  key: t.string,
  type: t.literal('RequestUpdateLeaderValue'),
  payload: t.object({
    value: t.unknown
  })
}), t.object({
  source: t.literal(POST_MESSAGE_SOURCE),
  key: t.string,
  type: t.literal('RequestPartialLeaderValueUpdate'),
  payload: t.object({
    delta: t.unknown
  })
}));
const isPostMessageEnvelope = data => t.validate(postMessageEnvelopeSchema, data);
const getBenderProjectLabel = () => {
  var _getHubSpotWindowInfo, _getHubSpotWindowInfo2;
  return (_getHubSpotWindowInfo = (_getHubSpotWindowInfo2 = getHubSpotWindowInfo(window)) === null || _getHubSpotWindowInfo2 === void 0 ? void 0 : _getHubSpotWindowInfo2.benderProject) !== null && _getHubSpotWindowInfo !== void 0 ? _getHubSpotWindowInfo : 'unknown-project';
};
const colorForString = value => {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) % 360;
  }
  return `hsl(${(hash + 360) % 360}, 65%, 40%)`;
};
const BADGE_BASE_STYLE = 'color:#fff;padding:1px 4px;border-radius:2px;font-weight:600;';
const buildLogPrefix = role => {
  const project = getBenderProjectLabel();
  const roleColor = role === 'leader' ? '#1e88e5' : '#43a047';
  return {
    format: `%cpost-message%c %c${project}%c %c${role}%c`,
    styles: [`background:#555;${BADGE_BASE_STYLE}`, '', `background:${colorForString(project)};${BADGE_BASE_STYLE}`, '', `background:${roleColor};${BADGE_BASE_STYLE}`, '']
  };
};
const logPostMessageEvent = (direction, role, key, type, extra) => {
  if (!devtoolsFlags.previewPostMessageLogging) {
    return;
  }
  const {
    format,
    styles
  } = buildLogPrefix(role);
  console.log(format, ...styles, Object.assign({
    direction,
    type,
    key
  }, extra));
};
const onSendLogger = (role, key, type, payload) => ({
  targetOrigin
}) => {
  logPostMessageEvent('send', role, key, type, {
    targetOrigin,
    payload
  });
};
/**
 * Creates a postMessage-based synchronization strategy for leader-follower stores.
 *
 * Role is determined by which factory method the caller invokes (`setupLeader`
 * vs `setupFollower`), not by the URL.
 *
 * Transport:
 * - The leader scans `<iframe>` elements in its document, parses each one's
 *   origin from `iframe.src`, and broadcasts only to frames whose parsed
 *   origin is in the trusted allow-list (using that exact origin as
 *   `targetOrigin`). It also posts to its own window so a follower in the
 *   same window — e.g. tests, or a sibling sub-tree — receives updates.
 * - The follower sends to `window.parent` (the host, when embedded in an
 *   iframe). The leader's exact origin is captured from the first inbound
 *   broadcast and reused as `targetOrigin` thereafter; until that arrives the
 *   follower fans out across every trusted origin so the initial
 *   `RequestNewLeaderValue` has a chance of reaching standard-origin leaders.
 *   It also posts to its own window so a same-window leader can receive.
 *
 * Trust checks and the actual `postMessage` calls live in
 * `utils/trustedPostMessage`; this module only adds envelope shape, schema
 * validation, and leader/follower-aware logging on top.
 */
export const postMessageStrategy = ({
  key,
  valueSchema
}) => {
  return {
    setupLeader: ({
      onNewLeaderValueRequest,
      onUpdateLeaderValueRequest,
      onPartialUpdateLeaderValueRequest
    }) => {
      const broadcastToFollowers = envelope => {
        const onSent = onSendLogger('leader', key, envelope.type, 'payload' in envelope ? envelope.payload : undefined);
        for (const {
          target,
          origin
        } of getTrustedChildFrameTargets()) {
          sendToFrame({
            target,
            targetOrigin: origin,
            message: envelope,
            onSent
          });
        }
        sendToSameWindow({
          message: envelope,
          onSent
        });
      };
      const handler = event => {
        if (event.source !== window && !isMessageFromChildFrame(event.source)) {
          return;
        }
        if (!isTrustedOrigin(event.origin)) {
          return;
        }
        if (!isPostMessageEnvelope(event.data)) {
          return;
        }
        if (event.data.key !== key) {
          return;
        }
        logPostMessageEvent('receive', 'leader', key, event.data.type, {
          origin: event.origin,
          payload: 'payload' in event.data ? event.data.payload : undefined
        });
        if (event.data.type === 'RequestNewLeaderValue') {
          broadcastToFollowers({
            source: POST_MESSAGE_SOURCE,
            key,
            type: 'ReportNewLeaderValue',
            payload: {
              value: onNewLeaderValueRequest(),
              storeVersion: STORE_VERSION
            }
          });
          return;
        }
        if (event.data.type === 'RequestUpdateLeaderValue') {
          prevalidatePublish({
            value: event.data.payload.value,
            schema: valueSchema,
            context: 'receiveRequestUpdateLeaderValue'
          });
          onUpdateLeaderValueRequest(event.data.payload.value);
          return;
        }
        if (event.data.type === 'RequestPartialLeaderValueUpdate') {
          onPartialUpdateLeaderValueRequest === null || onPartialUpdateLeaderValueRequest === void 0 || onPartialUpdateLeaderValueRequest(event.data.payload.delta);
          return;
        }
      };
      window.addEventListener('message', handler);
      broadcastToFollowers({
        source: POST_MESSAGE_SOURCE,
        key,
        type: 'ReportNewLeaderValue',
        payload: {
          value: onNewLeaderValueRequest(),
          storeVersion: STORE_VERSION
        }
      });
      return {
        reportNewLeaderValue: newValue => {
          prevalidatePublish({
            value: newValue,
            schema: valueSchema,
            context: 'reportNewLeaderValue'
          });
          broadcastToFollowers({
            source: POST_MESSAGE_SOURCE,
            key,
            type: 'ReportNewLeaderValue',
            payload: {
              value: newValue,
              storeVersion: STORE_VERSION
            }
          });
        },
        cleanup: () => {
          window.removeEventListener('message', handler);
        }
      };
    },
    setupFollower: ({
      onNewLeaderValue
    }) => {
      let leaderOrigin;
      let observedStoreVersion;
      let hasRecordedStoreVersion = false;
      let partialUpdatesEnabled = false;
      const sendToLeader = envelope => {
        const onSent = onSendLogger('follower', key, envelope.type, 'payload' in envelope ? envelope.payload : undefined);
        if (window.parent && window.parent !== window) {
          if (leaderOrigin) {
            sendToFrame({
              target: window.parent,
              targetOrigin: leaderOrigin,
              message: envelope,
              onSent
            });
          } else {
            // When same-origin (e.g. worktree local dev or standard same-origin
            // embed), read the parent's origin directly to avoid broadcasting to
            // every trusted origin and generating console errors for mismatches.
            let parentOrigin = null;
            try {
              const origin = readContentWindowOrigin(window.parent);
              if (isTrustedOrigin(origin)) {
                parentOrigin = origin;
              }
            } catch (_unused) {
              // Cross-origin parent — fall back to broadcast.
            }
            if (parentOrigin) {
              sendToFrame({
                target: window.parent,
                targetOrigin: parentOrigin,
                message: envelope,
                onSent
              });
            } else {
              sendToTrustedOrigins({
                target: window.parent,
                message: envelope,
                onSent
              });
            }
          }
        }
        sendToSameWindow({
          message: envelope,
          onSent
        });
      };
      const handler = event => {
        if (event.source !== window.parent && event.source !== window) {
          return;
        }
        if (!isTrustedOrigin(event.origin)) {
          return;
        }
        if (!isPostMessageEnvelope(event.data)) {
          return;
        }
        if (event.data.key !== key) {
          return;
        }
        if (event.data.type !== 'ReportNewLeaderValue') {
          return;
        }
        if (!leaderOrigin) {
          leaderOrigin = event.origin;
        }
        logPostMessageEvent('receive', 'follower', key, event.data.type, {
          origin: event.origin,
          payload: event.data.payload
        });
        if (!hasRecordedStoreVersion) {
          hasRecordedStoreVersion = true;
          observedStoreVersion = event.data.payload.storeVersion;
          const versionLabel = observedStoreVersion !== undefined ? String(observedStoreVersion) : 'legacy';
          Metrics.counter(`postmessage-strategy-leader-store-version-${versionLabel}`).increment();
          if (observedStoreVersion !== undefined && observedStoreVersion >= 1) {
            partialUpdatesEnabled = true;
          }
        }
        onNewLeaderValue(event.data.payload.value);
      };
      window.addEventListener('message', handler);
      sendToLeader({
        source: POST_MESSAGE_SOURCE,
        key,
        type: 'RequestNewLeaderValue'
      });
      return {
        requestUpdateLeaderValue: newValue => {
          prevalidatePublish({
            value: newValue,
            schema: valueSchema,
            context: 'requestUpdateLeaderValue'
          });
          sendToLeader({
            source: POST_MESSAGE_SOURCE,
            key,
            type: 'RequestUpdateLeaderValue',
            payload: {
              value: newValue
            }
          });
        },
        get requestPartialLeaderValueUpdate() {
          if (!partialUpdatesEnabled) {
            return undefined;
          }
          return delta => {
            sendToLeader({
              source: POST_MESSAGE_SOURCE,
              key,
              type: 'RequestPartialLeaderValueUpdate',
              payload: {
                delta
              }
            });
          };
        },
        cleanup: () => {
          window.removeEventListener('message', handler);
        }
      };
    }
  };
};